import React from "react";
import { Building2, RefreshCw } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { noWorkspaceTestIds } from "../testids/authTestIds";

/**
 * Terminal screen for a signed-in session that resolved no organisation.
 *
 * The session is valid — this is not an auth failure and must never be worded
 * as one. Without an org the gateway cannot scope a single request, so every
 * feature surface would 401; rendering the shell anyway is what produced
 * "Session expired. Please refresh the page." on a fresh account's connectors
 * page, over a session that was working fine.
 *
 * Two causes reach here and the client cannot tell them apart, because the
 * memberships endpoint answers the same 200 with an empty list either way:
 * an account deliberately given no workspace (competitor-domain signup), and a
 * provision that never completed. So the copy states only what is known, and
 * offers the action that fits both — retry, then ask a human.
 */
export function NoWorkspacePage(): React.ReactElement {
  const { logout, orgStatus, retryMemberships, user } = useAuth();
  const failed = orgStatus === "error";

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-background gap-4 p-6"
      data-testid={noWorkspaceTestIds.page}
    >
      <div
        className="max-w-md text-center bg-card rounded-xl p-8 border border-border"
        data-testid={noWorkspaceTestIds.card}
      >
        <div className="mb-4 flex justify-center">
          <Building2 size={48} strokeWidth={1.5} className="text-muted-foreground" />
        </div>
        <h2
          className="text-xl font-semibold text-foreground mb-2"
          data-testid={noWorkspaceTestIds.heading}
        >
          {failed ? "We couldn't load your workspace" : "No workspace yet"}
        </h2>
        <p
          className="text-sm text-muted-foreground leading-relaxed"
          data-testid={noWorkspaceTestIds.message}
        >
          {failed
            ? "Your sign-in worked, but we couldn't read which organization this account belongs to."
            : `This account isn't part of an organization yet, so there's nothing for it to open.`}
        </p>
        <p
          className="text-xs text-muted-foreground mt-4"
          data-testid={noWorkspaceTestIds.detail}
        >
          {failed
            ? "This is usually temporary. Try again, and contact us if it keeps happening."
            : "New accounts are normally set up within a few seconds. If this persists, your organization needs to be created for you."}
        </p>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            variant="default"
            onClick={retryMemberships}
            data-testid={noWorkspaceTestIds.retryButton}
          >
            <RefreshCw size={16} strokeWidth={1.5} />
            Try again
          </Button>
          <Button
            variant="outline"
            asChild
            data-testid={noWorkspaceTestIds.supportLink}
          >
            {/* Carries the address so support can find the account without
                asking for it — the one detail we reliably have here. */}
            <a href={`mailto:support@fastn.ai?subject=${encodeURIComponent("No workspace on my fastn account")}&body=${encodeURIComponent(`Account: ${user?.email ?? "unknown"}`)}`}>
              Contact support
            </a>
          </Button>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid={noWorkspaceTestIds.signOutButton}
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
